'use client';

import {
  type ArrayNode,
  type BuilderNode,
  type Decoration,
  facetId,
  type GroupNode,
  type LeafNode,
  type PickOption,
  type RuleBuilderSource,
  type SourceValues,
  type UseRuleBuilderOptions,
  whereConditions,
} from '@inixiative/rules-builder';
import { stripHtml } from '@zealot/shared';
import { isIncompleteValue } from '@zealot/shared/rules/segments';
import { alphabetical } from '@zealot/ui/lib/optionItems';
import { cn } from '@zealot/ui/lib/utils';
import { createContext, startTransition, useContext, useMemo } from 'react';

// gloss
export const commitDeferred = (fn: () => void) => startTransition(fn);

// gloss
export type RuleBuilderValue = NonNullable<UseRuleBuilderOptions['defaultValue']>;

// gloss
export const isRangeShape = (shape: string | undefined): boolean => shape === 'range' || shape === 'dateRange';

// gloss
// why: BigInt/Decimal leaf kinds must render as text and stay strings — a number input or
// why: Number() loses precision, and bigint can't JSON.stringify
export const isJsNumberKind = (kind?: string): boolean => kind === 'Int' || kind === 'Float';

// gloss
export const coerceLeafValue = (kind: string | undefined, raw: string): unknown =>
  raw === '' ? undefined : isJsNumberKind(kind) ? Number(raw) : raw;

export const fieldItems = (options: readonly PickOption[]) =>
  options.map(({ value, label, icon }) => ({ value, label, icon }));

// gloss
const SECTION_LABEL_MAX = 140;
export const sectionLabel = (groups: string[]): string => {
  const label = groups.map((group) => stripHtml(group)).join(' › ');
  return label.length > SECTION_LABEL_MAX ? `${label.slice(0, SECTION_LABEL_MAX - 1)}…` : label;
};

// gloss
export const sectionedItems = <T extends { label: string; groups?: string[] }>(
  options: readonly T[],
): (T & { group?: string })[] => {
  const grouped = options.map((option) => ({
    ...option,
    group: option.groups?.length ? sectionLabel(option.groups) : undefined,
  }));
  const partitions = new Set(grouped.filter((option) => option.group).map((option) => JSON.stringify(option.groups)));
  const hasUngrouped = grouped.some((option) => !option.group);
  const flatten = partitions.size === 0 || (partitions.size === 1 && !hasUngrouped);
  return alphabetical(flatten ? grouped.map((option) => ({ ...option, group: undefined })) : grouped);
};

// gloss
export type FacetGroups = {
  containers: { id: string; label: string; icon?: string }[];
  containerOf: Map<string, string>;
  facetIds: Set<string>;
};

export const EMPTY_GROUPS: FacetGroups = { containers: [], containerOf: new Map(), facetIds: new Set() };

// gloss
export const facetGroups = (decoration: Decoration | undefined, rootLabel?: string): FacetGroups => {
  const facets = decoration?.facets ?? [];
  if (!facets.length) return EMPTY_GROUPS;

  const containers: FacetGroups['containers'] = [];
  const containerOf = new Map<string, string>();
  const facetIds = new Set<string>();
  const sharedContainerIds = new Map<string, string>();
  for (const facet of facets) {
    const id = facetId(facet);
    facetIds.add(id);
    const home = (facet as { container?: string }).container;
    if (home === 'root' || (facet.label && facet.label === rootLabel)) {
      containerOf.set(id, '');
      continue;
    }
    if (home) {
      const sharedId = sharedContainerIds.get(home);
      if (sharedId) {
        containerOf.set(id, sharedId);
        continue;
      }
      sharedContainerIds.set(home, id);
      containers.push({ id, label: home, icon: facet.icon });
      containerOf.set(id, id);
      continue;
    }
    containers.push({ id, label: facet.label ?? id, icon: facet.icon });
    containerOf.set(id, id);
  }

  return { containers, containerOf, facetIds };
};

// gloss
export const availableContainers = (
  groups: FacetGroups,
  options: readonly { value: string }[],
): FacetGroups['containers'] =>
  groups.containers.filter((container) =>
    options.some((option) => groups.containerOf.get(option.value) === container.id),
  );

// gloss
export type BuilderScope = {
  groups: FacetGroups;
  rootLabel: string;
  rootIcon?: string;
  sentence?: boolean;
  sourceValues?: SourceValues[];
  source?: RuleBuilderSource;
  operatorLabels?: Record<string, string>;
  decoration?: Decoration;
  elementModel?: string;
  collapsible?: boolean;
};

// gloss
export const relabelElementFields = (
  options: readonly PickOption[],
  decoration: Decoration | undefined,
  model: string | undefined,
): readonly (PickOption & { hidden?: boolean })[] => {
  const fields = decoration?.labels?.fields;
  if (!model || !fields) return options;
  return options.map((option) => {
    const decor = fields[`${model}.${option.value}`] as { label?: string; hidden?: boolean } | undefined;
    return { ...option, label: decor?.label ?? option.label, hidden: decor?.hidden };
  });
};

// gloss
export const fieldDecorIcon = (
  decoration: Decoration | undefined,
  model: string | undefined,
  field: string | null | undefined,
): string | undefined => {
  if (!model || !field) return undefined;
  return (decoration?.labels?.fields?.[`${model}.${field}`] as { icon?: string } | undefined)?.icon;
};

// gloss
export const orderDecoratedFields = (
  source: RuleBuilderSource,
  decoration: Decoration | undefined,
): RuleBuilderSource => {
  const fields = decoration?.labels?.fields;
  if (!fields) return source;
  const decorOf = (model: string, field: string) =>
    fields[`${model}.${field}`] as { hidden?: boolean; order?: number } | undefined;
  const rank = (model: string, field: string): number => {
    const decor = decorOf(model, field);
    if (decor?.hidden) return Number.MAX_SAFE_INTEGER;
    return decor?.order ?? Number.MAX_SAFE_INTEGER - 1;
  };

  const src = source as RuleBuilderSource & {
    maps?: Record<string, { models?: Record<string, { fields?: Record<string, unknown> }> }>;
  };
  if (!src.maps) return source;

  let mapsChanged = false;
  const maps = Object.fromEntries(
    Object.entries(src.maps).map(([mapName, map]) => {
      let modelsChanged = false;
      const models = Object.fromEntries(
        Object.entries(map.models ?? {}).map(([modelName, model]) => {
          const names = Object.keys(model.fields ?? {});
          if (!names.some((name) => decorOf(modelName, name)?.hidden || decorOf(modelName, name)?.order != null)) {
            return [modelName, model];
          }
          const sorted = names
            .map((name, index) => ({ name, index }))
            .sort((a, b) => rank(modelName, a.name) - rank(modelName, b.name) || a.index - b.index)
            .map(({ name }) => [name, model.fields![name]] as const);
          modelsChanged = true;
          return [modelName, { ...model, fields: Object.fromEntries(sorted) }];
        }),
      );
      if (modelsChanged) mapsChanged = true;
      return [mapName, modelsChanged ? { ...map, models } : map];
    }),
  );

  return mapsChanged ? ({ ...src, maps } as RuleBuilderSource) : source;
};

// gloss
export const relabelOperators = (options: PickOption[], labels?: Record<string, string>): PickOption[] =>
  labels ? options.map((option) => ({ ...option, label: labels[option.value] ?? option.label })) : options;

// why: date field operators must be restricted to the day-precision set — full-timestamp equals
// why: never matches a picked day, and some ops don't compile at all
// gloss
const DATE_OPERATORS = new Set([
  'before',
  'notBefore',
  'after',
  'notAfter',
  'onOrBefore',
  'onOrAfter',
  'within',
  'notWithin',
  'between',
  'notBetween',
  'isEmpty',
  'notEmpty',
]);
export const curateOperators = (options: PickOption[], kind: string | undefined, current?: string): PickOption[] => {
  if (kind !== 'DateTime') return options;
  const seen = new Set<string>();
  return options.filter((option) => {
    if (seen.has(option.value) || (!DATE_OPERATORS.has(option.value) && option.value !== current)) return false;
    seen.add(option.value);
    return true;
  });
};

// gloss
export const findDateWindowLeaf = (group: GroupNode | undefined): LeafNode | undefined =>
  (group?.children ?? []).find(
    (child): child is LeafNode => child.kind === 'leaf' && child.value?.shape === 'dateWindow',
  );

export const BuilderContext = createContext<BuilderScope>({ groups: EMPTY_GROUPS, rootLabel: '' });

// gloss
export type BuilderMutate = { value?: RuleBuilderValue; setCondition?: (next: RuleBuilderValue) => void };

export const MutateContext = createContext<BuilderMutate>({});

// gloss
export const useNestedScope = (node: ArrayNode): BuilderScope => {
  const parent = useContext(BuilderContext);
  return useMemo(
    () => ({
      ...parent,
      groups: EMPTY_GROUPS,
      rootLabel: '',
      rootIcon: undefined,
      elementModel: node.relation?.modelName,
    }),
    [parent, node.relation?.modelName],
  );
};

export type SelectorSpec = NonNullable<ArrayNode['selectors']>[number];

export type LeafCondition = { field?: unknown; operator?: string; value?: unknown };

// gloss
export const lockedConditions = (node: ArrayNode, decoration: Decoration | undefined): LeafCondition[] => {
  const hoistId = node.hoist?.id;
  if (!hoistId || !decoration) return [];
  const facet = decoration.facets.find((entry) => facetId(entry) === hoistId);
  return whereConditions(facet?.where).flatMap((clause) => {
    const leaf = clause as { field?: unknown; operator?: string; value?: unknown };
    return leaf.field ? [{ field: leaf.field, operator: leaf.operator, value: leaf.value }] : [];
  });
};

export const clauseAdmits = (axisValue: string | undefined, clause: LeafCondition): boolean => {
  if (axisValue === undefined) return false;
  if (clause.operator === 'equals') return axisValue === clause.value;
  if (clause.operator === 'in') return Array.isArray(clause.value) && clause.value.includes(axisValue);
  if (clause.operator === 'notIn') return !(Array.isArray(clause.value) && clause.value.includes(axisValue));
  if (clause.operator === 'notEquals') return axisValue !== clause.value;
  return true;
};

// gloss
export const sourceAxes = (source: RuleBuilderSource | undefined, sv: SourceValues): readonly string[] | undefined => {
  const stamped = (sv as { groupBy?: readonly string[] }).groupBy;
  if (stamped) return stamped;
  const surface = source as
    | {
        maps?: Record<
          string,
          { models?: Record<string, { fields?: Record<string, { groupBy?: readonly string[] }> }> }
        >;
      }
    | undefined;
  return surface?.maps?.[sv.mapName]?.models?.[sv.model]?.fields?.[sv.field]?.groupBy;
};

// gloss
const toSelectorOptions = (options: SourceValues['options'] | undefined): PickOption[] =>
  (options ?? []).map((option) => ({
    value: String(option.value),
    label: option.label ?? String(option.value),
    ...(option.groups?.length ? { groups: option.groups } : {}),
  }));

// gloss
const dedicatedOptions = (
  sourceValues: SourceValues[] | undefined,
  relation: string | undefined,
  field: string,
): PickOption[] => {
  if (!relation) return [];
  // why: vocabulary lookup must be path-anchored, not tail-matched — tail matching lets an
  // why: unrelated same-tail field's data leak in (ZLT-3849)
  const segments = field.split('.');
  const tail = segments.pop();
  const dedicatedPath = [relation, ...segments].join('.');
  for (const sv of sourceValues ?? []) {
    if (sv.field !== tail || !sv.path) continue;
    const path = String(sv.path);
    if (path !== dedicatedPath && !path.endsWith(`.${dedicatedPath}`)) continue;
    const options = toSelectorOptions(sv.options);
    if (options.length) return options;
  }
  return [];
};

// gloss
export const selectorItems = (
  sourceValues: SourceValues[] | undefined,
  source: RuleBuilderSource | undefined,
  node: ArrayNode,
  selector: SelectorSpec,
  decoration: Decoration | undefined,
): PickOption[] => {
  const relation = node.field.value;
  const identity = lockedConditions(node, decoration);

  const dedicated = dedicatedOptions(sourceValues, relation, selector.field);
  if (dedicated.length) return dedicated;

  let sawAxis = false;
  for (const sv of sourceValues ?? []) {
    const axes = sourceAxes(source, sv);
    const axis = axes?.indexOf(selector.field) ?? -1;
    if (axis < 0 || (relation && sv.path && !String(sv.path).split('.').includes(relation))) continue;
    sawAxis = true;
    const admitted = new Set<string>();
    for (const option of sv.options ?? []) {
      const groups = (option as { groups?: string[] }).groups;
      const constrained = identity.every((clause) => {
        const clauseAxis = axes?.indexOf(String(clause.field)) ?? -1;
        return clauseAxis < 0 || clauseAdmits(groups?.[clauseAxis], clause);
      });
      if (constrained && groups?.[axis] !== undefined) admitted.add(groups[axis]);
    }
    if (admitted.size) {
      return [...admitted].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value }));
    }
  }
  if (sawAxis) return [];

  const tail = selector.field.split('.').pop();
  for (const sv of sourceValues ?? []) {
    if (sv.field !== tail) continue;
    if (relation && sv.path && !String(sv.path).split('.').includes(relation)) continue;
    const options = toSelectorOptions(sv.options);
    if (options.length) return options;
  }
  return [];
};

// gloss
export const selectorClause = (node: ArrayNode, selector: SelectorSpec): LeafNode | undefined =>
  (node.selectorClauses ?? []).find(
    (clause): clause is LeafNode => clause.kind === 'leaf' && clause.field?.value === selector.field,
  ) ??
  (node.condition?.children ?? []).find(
    (child): child is LeafNode => child.kind === 'leaf' && child.field?.value === selector.field,
  );

// gloss
export const selectorMultiCount = (node: ArrayNode, selector: SelectorSpec): number | undefined => {
  const block = (node.selectorClauses ?? []).find(
    (clause): clause is GroupNode =>
      clause.kind === 'group' &&
      clause.children.some((child) => child.kind === 'leaf' && child.field?.value === selector.field),
  );
  if (block) return block.children.length;
  const leaf = selectorClause(node, selector);
  return Array.isArray(leaf?.value?.current) ? leaf.value.current.length : undefined;
};

// gloss
export const selectorIsVisible = (
  node: ArrayNode,
  selector: SelectorSpec,
  sourceValues: SourceValues[] | undefined,
  source: RuleBuilderSource | undefined,
  decoration: Decoration | undefined,
): boolean =>
  selectorItems(sourceValues, source, node, selector, decoration).length > 0 || !!selectorClause(node, selector);

// gloss
export const conditionSummary = (leaf: BuilderNode, scope: BuilderScope, fieldLabel?: string): string | null => {
  if (leaf.kind !== 'leaf') return null;
  if (leaf.leafKind === 'boolean') return leaf.literal?.value ? 'Always' : 'Never';
  if (!leaf.field) return null;
  const rawField = String(leaf.field.value ?? '');
  const [relabeled] = relabelElementFields(
    [{ value: rawField, label: leaf.field.options.find((o) => o.value === rawField)?.label ?? rawField }],
    scope.decoration,
    scope.elementModel,
  );
  const label = fieldLabel ?? relabeled?.label ?? rawField;
  const operator = leaf.operator?.value;
  const operatorLabel = (operator && scope.operatorLabels?.[operator]) ?? operator ?? '';
  const current = leaf.value?.current;
  const resolveValue = (raw: unknown): string =>
    leaf.value?.options?.find((option) => String(option.value) === String(raw))?.label ?? String(raw);
  const isRange = isRangeShape(leaf.value?.shape);
  const shown = Array.isArray(current)
    ? isRange
      ? current.map(resolveValue).join(' and ')
      : current.length <= 3
        ? current.map(resolveValue).join(', ')
        : `${current.slice(0, 2).map(resolveValue).join(', ')} +${current.length - 2} more`
    : resolveValue(current ?? '');
  return `${label} ${operatorLabel} ${shown}`.trim();
};

// gloss
export const isLeafComplete = (leaf?: LeafNode): boolean => {
  if (!leaf) return false;
  if (leaf.leafKind === 'boolean') return true;
  if (!leaf.field?.value || !leaf.operator?.value) return false;
  const current = leaf.value?.current;
  // why: the shared predicate leaves a missing value to validateRule, which this gate has no
  // why: access to — so nullish stays a local check
  return current != null && !isIncompleteValue(current);
};

// gloss
export const WINDOW_CHOICES: { key: string; label: string; expr?: Record<string, unknown> }[] = [
  { key: 'thisYear', label: 'this year', expr: { this: 'year' } },
  { key: 'lastYear', label: 'last year', expr: { last: 'year' } },
  { key: 'last30Days', label: 'in the last 30 days', expr: { ago: { days: 30 } } },
  { key: 'last60Days', label: 'in the last 60 days', expr: { ago: { days: 60 } } },
  { key: 'last90Days', label: 'in the last 90 days', expr: { ago: { days: 90 } } },
  { key: 'last180Days', label: 'in the last 180 days', expr: { ago: { days: 180 } } },
  { key: 'last365Days', label: 'in the last 365 days', expr: { ago: { days: 365 } } },
  { key: 'allTime', label: 'all time' },
];

// why: walks every segment, because a relation reached through another one is spelled as a path —
// why: `brandMission.missionTagAssignments`. A single-segment lookup returns nothing for those, and
// why: the caller then humanises the raw path into the admin's face (ZLT-4825).
export const relationModelName = (source: RuleBuilderSource | undefined, relation: string): string | undefined => {
  const models = source?.maps?.[source.mapName]?.models;
  if (!models || !source) return undefined;
  let model: string = source.model;
  for (const segment of relation.split('.')) {
    const field = models[model]?.fields?.[segment];
    const type = field && typeof field === 'object' && 'type' in field ? (field as { type?: unknown }).type : undefined;
    if (typeof type !== 'string') return undefined;
    model = type;
  }
  return model;
};

// gloss
const elementFieldOwner = (
  source: RuleBuilderSource | undefined,
  elementModel: string | undefined,
  path: string,
): { model: string; field: string } | undefined => {
  const segments = path.split('.');
  const field = segments.pop();
  if (!field || !elementModel) return undefined;
  let model = elementModel;
  for (const segment of segments) {
    const hop = source?.maps?.[source.mapName]?.models?.[model]?.fields?.[segment];
    const type = hop && typeof hop === 'object' && 'type' in hop ? (hop as { type?: unknown }).type : undefined;
    if (typeof type !== 'string') return undefined;
    model = type;
  }
  return { model, field };
};

// gloss
export const decoratedFieldLabel = (scope: BuilderScope, path: string): string | undefined => {
  const owner = elementFieldOwner(scope.source, scope.elementModel, path);
  if (!owner) return undefined;
  const decor = scope.decoration?.labels?.fields?.[`${owner.model}.${owner.field}`] as { label?: string } | undefined;
  return decor?.label;
};

// gloss
export const decoratedRelationLabel = (scope: BuilderScope, relation: string): string | undefined => {
  const model = relationModelName(scope.source, relation);
  if (!model) return undefined;
  const decor = scope.decoration?.labels?.models?.[model] as { label?: string } | undefined;
  return decor?.label;
};

// gloss
export const lockedConditionSummary = (clause: LeafCondition, scope: BuilderScope, relation?: string): string => {
  const rawField = String(clause.field ?? '');
  const label =
    decoratedFieldLabel(scope, rawField) ??
    elementFieldOwner(scope.source, scope.elementModel, rawField)?.field ??
    rawField;
  const operator = clause.operator ?? 'equals';
  const operatorLabel = scope.operatorLabels?.[operator] ?? operator;
  const valueLabels = new Map(
    dedicatedOptions(scope.sourceValues, relation, rawField).map((option) => [option.value, option.label]),
  );
  const shownValue = (each: unknown) => valueLabels.get(String(each)) ?? String(each ?? '');
  const value = clause.value;
  const shown = Array.isArray(value) ? value.map(shownValue).join(', ') : shownValue(value);
  return `${label} ${operatorLabel} ${shown}`.trim();
};

export const presetRowClasses = (sentence: boolean | undefined): string =>
  cn('flex flex-wrap items-center gap-2', !sentence && 'rounded-md border border-border bg-card px-2.5 py-2 shadow-sm');

export { alphabetical };
